import { Head, Link, router } from '@inertiajs/react'
import React, { useState } from 'react'
import Layout from './Layout'
import RoomCard from './Components/RoomCard'
import EmptyState from '../../Components/EmptyState'
import PhoneButton from '../../Components/PhoneButton'

// Static ranges — the selected range lives in local page state only; just the
// range max travels onward as party_size ("100+" passes 100).
const PARTY_SIZE_RANGES = [
  { label: 'Up to 30', max: 30 },
  { label: '30-60', max: 60 },
  { label: '60-100', max: 100 },
  { label: '100+', max: 100 }
]

export default function EventType({ account, event_type, venues, meta }) {
  const [selectedRange, setSelectedRange] = useState(null)

  const roomHref = (venue) => {
    const base = `/${account.slug}/booking/venues/${venue.id}`
    return selectedRange ? `${base}?party_size=${selectedRange.max}` : base
  }

  const fitsRange = (venue) => {
    if (!selectedRange || !venue.capacity) return null
    return venue.capacity >= selectedRange.max
  }

  const fittingRooms = venues.filter(v => fitsRange(v) !== false)
  const smallRooms = venues.filter(v => fitsRange(v) === false)

  return (
    <>
      <Head title={meta.title} />
      <Layout account={account} wide>
        <div className="px-4 pt-4 pb-2">
          <Link
            href={`/${account.slug}/booking`}
            className="text-sky-200 hover:text-white text-sm"
          >
            &larr; All occasions
          </Link>
        </div>

        <div className="px-4 pb-4">
          <h1 className="text-2xl font-bold text-white">{event_type.name}</h1>
          {event_type.tagline && (
            <p className="text-sky-200 mt-1">{event_type.tagline}</p>
          )}
          {event_type.description && (
            <p className="text-sky-200/80 text-sm mt-2 line-clamp-2">{event_type.description}</p>
          )}
        </div>

        {venues.length > 0 && (
          <div className="px-4 pb-4">
            <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Roughly how many guests?">
              {PARTY_SIZE_RANGES.map(range => {
                const selected = selectedRange?.label === range.label
                return (
                  <button
                    key={range.label}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedRange(selected ? null : range)}
                    className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selected
                        ? 'bg-white text-sky-900'
                        : 'bg-sky-800 text-sky-100 hover:bg-sky-700'
                    }`}
                  >
                    {range.label}
                  </button>
                )
              })}
            </div>
            <p className="text-sky-200/70 text-xs mt-2">Roughly how many guests? Not sure? Skip this.</p>
          </div>
        )}

        {venues.length > 0 ? (
          <div className="px-4 pb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[...fittingRooms, ...smallRooms].map(venue => (
                <RoomCard
                  key={venue.id}
                  venue={venue}
                  href={roomHref(venue)}
                  fit={fitsRange(venue)}
                  rangeLabel={selectedRange?.label}
                  partySizeLabel={selectedRange ? `~${selectedRange.max}` : null}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <EmptyState
              emoji="📅"
              title="No rooms available right now"
              description={`We don't have rooms bookable online for this occasion at the moment — our team can still help you plan it.`}
              actionLabel="Browse other occasions"
              onAction={() => router.visit(`/${account.slug}/booking`)}
            />
            {account.phone && (
              <div className="px-4 pb-8 max-w-md mx-auto">
                <PhoneButton phoneNumber={account.phone}>
                  Call us to plan your event
                </PhoneButton>
              </div>
            )}
          </>
        )}
      </Layout>
    </>
  )
}
